import { memo, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useProductStoreContext } from "../new/ProductStoreContext";
import { SupplyPartRenderer } from "../new/SupplyPartRenderer";

/** Piano-hinge part definition: type, positions[]. */
interface PianoHingePartDef {
  type: string;
  positions?: Array<{ x: number; y: number; z: number }>;
}

/**
 * Thin wrapper: renders the same part (mesh) at each position in part.positions
 * by calling SupplyPartRenderer multiple times in positioned groups.
 * Same pattern as AddableRenderer (which uses SupplyPartRenderer), just N times.
 */
const PianoHingeRendererImpl = ({ id }: { id: string }) => {
  const useProductStore = useProductStoreContext();

  const partDef = useProductStore(
    useShallow((state) => {
      if (!state.product?.partDefinitions || !id) return null;
      return state.product.partDefinitions.get(id) as
        | PianoHingePartDef
        | undefined;
    }),
  );

  const isPianoHinge =
    partDef && (partDef as PianoHingePartDef).type === "PianoHinge";
  const pianoDef = isPianoHinge ? (partDef as PianoHingePartDef) : null;

  const localOffsets = useMemo((): ReadonlyArray<
    readonly [number, number, number]
  > => {
    const positions = pianoDef?.positions;
    if (!positions || positions.length === 0) return [[0, 0, 0]];
    const base = positions[0];
    if (!base) return [[0, 0, 0]];
    return positions.map(
      (p) => [p.x - base.x, p.y - base.y, p.z - base.z] as const,
    );
  }, [pianoDef?.positions]);

  if (!isPianoHinge || !pianoDef?.positions?.length) {
    return <group />;
  }

  // Same rotation as AddableRenderer: 90° around X
  const addableRotation: [number, number, number] = [Math.PI / 2, 0, 0];

  return (
    <group>
      {localOffsets.map((offset) => (
        <group
          key={`${offset[0]}-${offset[1]}-${offset[2]}`}
          position={[offset[0], offset[1], offset[2]]}
          rotation={addableRotation}
        >
          <SupplyPartRenderer id={id} />
        </group>
      ))}
    </group>
  );
};

export const PianoHingeRenderer = memo(PianoHingeRendererImpl);
